// MCHD Room Scheduler - Interactive Floor Plan Canvas with Konva
// Interactive floor plan visualization with room scheduling capabilities
// Modified: 08-10-2025 11:27:08 - Added real-time sync, conflict pulse, enhanced tooltips

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Stage, Layer, Rect, Text, Line, Group } from '../../lib/konva-optimized';
import { Box, Paper, Stack, Text as MantineText, Loader, Center, Button } from '@mantine/core';
import { IconCalendar, IconEdit, IconPlus } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';

// Import our new components
import FloorPlanToolbar from '../molecules/FloorPlanToolbar';
import FloorPlanLegend from '../molecules/FloorPlanLegend';
import CapacityBar from '../molecules/CapacityBar';
import RoomContextMenu, { ContextMenuAction, RoomForContextMenu } from '../molecules/RoomContextMenu';
import OffCanvasRoomList from './OffCanvasRoomList';
import { EmptyStateIllustration } from '../molecules/EmptyStateIllustration';
import { AnimatedButton } from '../atoms/AnimatedButton';
import { RoomNode } from './floor-plan/RoomNode';
import { ConflictSuggestion, ContextMenuPosition } from '../../types/planner';
import { 
  calculateRoomBoundsCenter, 
  calculateCenteredStagePosition,
  getRoomOpacityForLegendFilter,
  getShadowConfig
} from '../../utils/canvasHelpers';
import { useRoomDoubleClick } from '../../hooks/useRoomDoubleClick';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { FloorPlanEditor } from '../../features/coordinator/floor-plan-editor';
import { useAuth } from '../../hooks/useAuth';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface FloorPlanRoom {
  id: string;
  number: string;
  name: string;
  capacity: number;
  isActive: boolean;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  assignments: Array<{
    id: string;
    title: string;
    status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
    user: {
      id: string;
      name: string;
    };
    sessionTime: {
      id: string;
      name: string;
      startTime: string;
      endTime: string;
    };
  }>;
  blocks: Array<{
    id: string;
    name: string;
    startDate: string;
    endDate: string;
  }>;
  // NEW: Conflict data for visualization
  conflicts?: Array<{
    code: string;
    message: string;
    severity: 'CRITICAL' | 'WARNING' | 'INFO';
  }>;
}

interface FloorPlanData {
  floor: {
    id: string;
    name: string;
    building: {
      id: string;
      name: string;
    };
  };
  rooms: FloorPlanRoom[];
  sessionTimes: Array<{
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  }>;
}

interface FloorPlanCanvasProps {
  data: FloorPlanData | null;
  selectedDate?: Date;
  onRoomClick?: (room: FloorPlanRoom) => void;
  onAssignmentClick?: (assignment: any, room: FloorPlanRoom) => void;
  loading?: boolean;
  error?: string;
  showToolbar?: boolean;
  showLegend?: boolean;
  enableGridSnap?: boolean;
  enableFullscreen?: boolean;
  // NEW: Modern 2025 UX Flow props
  enableRealTimeSync?: boolean; // Auto-refresh on data changes (default: true)
  conflictSuggestions?: Map<string, ConflictSuggestion>; // Conflict suggestions from API
  legendPosition?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  legendAlwaysVisible?: boolean;
  // PHASE 3: Event handlers per Spec Section 3
  onRoomDoubleClick?: (room: FloorPlanRoom) => void; // Double-click opens Assign modal
  onContextMenuAction?: (action: ContextMenuAction, room: FloorPlanRoom) => void; // Right-click menu actions
  // PHASE 4: Legend filtering per Spec Section 3
  activeLegendFilters?: string[]; // Active legend filter IDs for visual dimming
  onLegendFilterChange?: (filterId: string) => void; // Toggle legend filter
  onClearLegendFilters?: () => void; // Clear all legend filters
  // Canvas mode props
  mode?: 'view' | 'edit'; // Canvas mode (default: 'view')
  onEnterEditMode?: () => void; // Callback to enter edit mode from empty state
  onCreateRoom?: () => void; // Callback to create a new room from empty state
}

// =============================================================================
// CONSTANTS AND UTILITIES
// =============================================================================

/**
 * ROOM_COLORS - Standard color palette for room statuses
 * 
 * IMPORTANT: These colors MUST match FloorPlanLegend defaultLegendItems.
 * Any changes here should be reflected in:
 * - components/molecules/FloorPlanLegend.tsx (defaultLegendItems)
 * - pages/coordinator/EnhancedPlannerPage.tsx (FloorPlanLegend items)
 * 
 * @updated 14-10-2025 - Added conflict color for consistency
 */
const ROOM_COLORS = {
  available: '#e8f5e8',  // Light green - room ready for assignment
  occupied: '#ffeaa7',   // Light yellow - room has assignments
  blocked: '#fab1a0',    // Light red - room blocked (maintenance/cleaning)
  inactive: '#ddd',      // Gray - room out of service
  conflict: '#fa5252',   // Red - room has scheduling conflicts
  selected: '#74b9ff'    // Blue - room is currently selected
};

/**
 * ASSIGNMENT_STATUS_COLORS - Colors for assignment status indicators
 * 
 * NOTE: Only CONFIRMED and CANCELLED exist in current database schema.
 * Legacy PENDING and COMPLETED removed via migration 20251011062054.
 * 
 * @updated 14-10-2025 - Added documentation for removed statuses
 */
const ASSIGNMENT_STATUS_COLORS = {
  CONFIRMED: '#00b894',   // Green - assignment confirmed and scheduled
  CANCELLED: '#e17055'    // Red - assignment cancelled
};

const CANVAS_CONFIG = {
  width: 1200,
  height: 800,
  gridSize: 20,
  minZoom: 0.1,
  maxZoom: 5,
  zoomStep: 1.2,
  snapThreshold: 10
};

// Performance optimization constants
const BATCH_DRAW_THROTTLE = 100; // Batch draws every 100ms

/**
 * Get room status color based on assignments and blocks
 * @param room - Room data
 * @param selectedDate - Selected date
 * @returns Color string
 */
function getRoomStatusColor(room: FloorPlanRoom, selectedDate?: Date): string {
  if (!room.isActive) return ROOM_COLORS.inactive;
  
  if (room.blocks.length > 0) {
    if (selectedDate) {
      const hasBlockOnDate = room.blocks.some(block => {
        const startDate = new Date(block.startDate);
        const endDate = new Date(block.endDate);
        return selectedDate >= startDate && selectedDate <= endDate;
      });
      if (hasBlockOnDate) return ROOM_COLORS.blocked;
    }
  }
  
  if (room.assignments.length > 0) {
    return ROOM_COLORS.occupied;
  }
  
  return ROOM_COLORS.available;
}

/**
 * Check if room has conflicts
 * NEW: 08-10-2025 11:27:08 - Conflict detection for pulse animation
 * @param room - Room data
 * @returns True if room has conflicts
 */
function hasConflicts(room: FloorPlanRoom): boolean {
  // Check if room has conflict data
  if (room.conflicts && room.conflicts.length > 0) {
    return true;
  }
  
  // Check for capacity conflicts
  if (room.assignments.length > room.capacity) {
    return true;
  }
  
  // Check for cancelled assignments (indicator of conflict)
  const hasCancelledAssignments = room.assignments.some(
    (assignment) => assignment.status === 'CANCELLED'
  );
  
  return hasCancelledAssignments;
}

// Removed unused formatTime function

// =============================================================================
// KONVA COMPONENTS
// =============================================================================



/**
 * Grid component for Konva canvas
 */
const GridComponent: React.FC = () => {
  const lines = [];
  
  // Vertical lines
  for (let i = 0; i <= CANVAS_CONFIG.width; i += CANVAS_CONFIG.gridSize) {
    lines.push(
      <Line
        key={`v-${i}`}
        points={[i, 0, i, CANVAS_CONFIG.height]}
        stroke="#f0f0f0"
        strokeWidth={1}
      />
    );
  }
  
  // Horizontal lines
  for (let i = 0; i <= CANVAS_CONFIG.height; i += CANVAS_CONFIG.gridSize) {
    lines.push(
      <Line
        key={`h-${i}`}
        points={[0, i, CANVAS_CONFIG.width, i]}
        stroke="#f0f0f0"
        strokeWidth={1}
      />
    );
  }
  
  return <>{lines}</>;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Interactive Floor Plan Canvas Component
 * Provides Konva-based floor plan visualization with room scheduling capabilities
 * 
 * Enhanced Features:
 * - Performance optimized with batchDraw and layer management
 * - Advanced toolbar with zoom controls and grid snap
 * - Always-visible legend with state indicators
 * - Smooth animations and interactions
 * - Mobile touch support
 * - Real-time conflict visualization
 */
export const FloorPlanCanvas: React.FC<FloorPlanCanvasProps> = ({
  data,
  selectedDate,
  onRoomClick,
  onAssignmentClick,
  loading = false,
  error,
  showToolbar = true,
  showLegend = true,
  enableRealTimeSync = true,
  conflictSuggestions = new Map(),
  legendPosition = 'bottom-right',
  legendAlwaysVisible = true,
  onRoomDoubleClick,
  onContextMenuAction,
  activeLegendFilters = [],
  onLegendFilterChange,
  onClearLegendFilters,
  mode = 'view',
  onEnterEditMode,
  onCreateRoom
}) => {
  // Get current user for role-based permissions
  const { user } = useAuth();
  const canEditLayout = user?.role === 'ADMIN' || user?.role === 'COORDINATOR';

  const [selectedRoom, setSelectedRoom] = useState<FloorPlanRoom | null>(null);
  const [stageScale, setStageScale] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [gridSnapEnabled, setGridSnapEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Handle mode prop changes
  useEffect(() => {
    if (mode === 'edit') {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
  }, [mode]);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    text: string;
    conflict?: ConflictSuggestion; // NEW: Conflict data for tooltip
  }>({ visible: false, x: 0, y: 0, text: '' });
  
  // PHASE 3: Context menu state per Spec Section 3
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition | null>(null);
  
  // NEW: Responsive canvas dimensions (maintains 3:2 aspect ratio)
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: CANVAS_CONFIG.width,
    height: CANVAS_CONFIG.height
  });
  
  const stageRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const batchDrawTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined); // PHASE 3: 120ms tooltip delay
  const queryClient = useQueryClient(); // NEW: For real-time sync
  
  // Performance optimization: Batch draw function
  const batchDraw = useCallback(() => {
    if (batchDrawTimeoutRef.current) {
      clearTimeout(batchDrawTimeoutRef.current);
    }
    
    batchDrawTimeoutRef.current = setTimeout(() => {
      if (layerRef.current) {
        layerRef.current.batchDraw();
      }
    }, BATCH_DRAW_THROTTLE);
  }, []);
  
  // NEW: Responsive canvas dimensions - Fill container height, keep within max spec
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;

        // Fit entirely into container (no cropping), but cap to max canvas config
        const width = Math.min(containerWidth, CANVAS_CONFIG.width);
        const height = Math.min(containerHeight, CANVAS_CONFIG.height);

        setCanvasDimensions({ width, height });
      }
    };
    
    // Initial calculation
    updateDimensions();
    
    // Listen for resize events
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  
  // NEW: Real-time sync - Subscribe to React Query cache changes
  useEffect(() => {
    if (!enableRealTimeSync) return;
    
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // When plannerGrid or floorPlan queries are invalidated, trigger redraw
      if (event?.type === 'updated' && event.query.queryKey[0] === 'floorPlan') {
        batchDraw();
      }
    });
    
    return unsubscribe;
  }, [enableRealTimeSync, queryClient, batchDraw]);
  
  /**
   * Performance Optimization: Cache room shapes for faster rendering
   * From Konva docs: "Cache node to improve drawing performance"
   * @created 16-10-2025 13:43:52
   */
  useEffect(() => {
    if (!layerRef.current || !data?.rooms) return;
    
    // Cache all room shapes after initial render
    const timer = setTimeout(() => {
      layerRef.current?.find('Group').forEach((group: any) => {
        // Cache the group if not already cached
        if (!group.isCached()) {
          group.cache();
        }
      });
      layerRef.current?.batchDraw();
    }, 100); // Small delay to ensure shapes are fully rendered
    
    return () => clearTimeout(timer);
  }, [data?.rooms?.length]); // Re-cache when room count changes
  
  /**
   * Clear and re-cache shapes when room data changes
   * @created 16-10-2025 13:43:52
   */
  useEffect(() => {
    if (!layerRef.current || !data?.rooms) return;
    
    // Clear cache and re-cache when room details change
    layerRef.current?.find('Group').forEach((group: any) => {
      if (group.isCached()) {
        group.clearCache();
        group.cache();
      }
    });
    layerRef.current?.batchDraw();
  }, [data?.rooms]);
  
  // NEW: Center canvas on first active room at 100% zoom on mount
  // Per Floor Plan Spec Section 2: Entry state
  useEffect(() => {
    if (!data || !data.rooms || data.rooms.length === 0) return;
    
    const center = calculateRoomBoundsCenter(data.rooms);
    if (!center) return;
    
    // Calculate stage position to center on first active room
    const centeredPosition = calculateCenteredStagePosition(
      center,
      canvasDimensions.width,
      canvasDimensions.height,
      1.0 // 100% zoom per spec
    );
    
    setStageScale(1.0);
    setStagePosition(centeredPosition);
    batchDraw();
    
  }, [data?.rooms, canvasDimensions.width, canvasDimensions.height, batchDraw]);

  // PHASE 3: Double-click detection per Spec Section 3
  const { handleClick: handleRoomClickWithDouble } = useRoomDoubleClick<FloorPlanRoom>({
    onSingleClick: useCallback((room) => {
      setSelectedRoom(room);
      onRoomClick?.(room);
      batchDraw();
    }, [onRoomClick, batchDraw]),
    onDoubleClick: useCallback((room) => {
      onRoomDoubleClick?.(room);
    }, [onRoomDoubleClick]),
    delay: 300 // 300ms window for double-click detection
  });
  
  // Handle room selection (single click)
  const handleRoomClick = useCallback((room: FloorPlanRoom) => {
    handleRoomClickWithDouble(room);
  }, [handleRoomClickWithDouble]);
  
  // PHASE 3: Right-click context menu per Spec Section 3
  const handleRoomContextMenu = useCallback((e: any, room: FloorPlanRoom) => {
    e.evt.preventDefault(); // Prevent default browser context menu
    
    const stage = e.target.getStage();
    const mousePos = stage.getPointerPosition();
    
    if (mousePos) {
      setContextMenuPosition({
        x: mousePos.x,
        y: mousePos.y,
        roomId: room.id
      });
    }
  }, []);
  
  // Handle assignment click
  const handleAssignmentClick = useCallback((assignment: any, room: FloorPlanRoom) => {
    onAssignmentClick?.(assignment, room);
    batchDraw();
  }, [onAssignmentClick, batchDraw]);
  
  // Handle stage drag end with grid snap
  const handleStageDragEnd = useCallback((e: any) => {
    let newX = e.target.x();
    let newY = e.target.y();
    
    if (gridSnapEnabled) {
      newX = Math.round(newX / CANVAS_CONFIG.gridSize) * CANVAS_CONFIG.gridSize;
      newY = Math.round(newY / CANVAS_CONFIG.gridSize) * CANVAS_CONFIG.gridSize;
    }
    
    setStagePosition({ x: newX, y: newY });
    batchDraw();
  }, [gridSnapEnabled, batchDraw]);
  
  // Enhanced wheel zoom with performance optimization
  const handleWheel = useCallback((e: any) => {
    e.evt.preventDefault();
    
    const stage = stageRef.current;
    if (!stage) return;
    
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    
    if (!pointer) return;
    
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale
    };
    
    const newScale = e.evt.deltaY > 0 
      ? Math.max(oldScale / CANVAS_CONFIG.zoomStep, CANVAS_CONFIG.minZoom)
      : Math.min(oldScale * CANVAS_CONFIG.zoomStep, CANVAS_CONFIG.maxZoom);
    
    setStageScale(newScale);
    setStagePosition({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale
    });
    
    batchDraw();
  }, [batchDraw]);

  // Toolbar event handlers
  const handleZoomIn = useCallback(() => {
    const newScale = Math.min(stageScale * CANVAS_CONFIG.zoomStep, CANVAS_CONFIG.maxZoom);
    setStageScale(newScale);
    batchDraw();
  }, [stageScale, batchDraw]);

  const handleZoomOut = useCallback(() => {
    const newScale = Math.max(stageScale / CANVAS_CONFIG.zoomStep, CANVAS_CONFIG.minZoom);
    setStageScale(newScale);
    batchDraw();
  }, [stageScale, batchDraw]);

  const handleResetView = useCallback(() => {
    setStageScale(1);
    setStagePosition({ x: 0, y: 0 });
    batchDraw();
  }, [batchDraw]);

  // Auto-fit content to container: compute scale & center so 100% of canvas content is visible
  const fitContentToViewport = useCallback(() => {
    if (!data?.rooms || data.rooms.length === 0) {
      setStageScale(1);
      setStagePosition({ x: 0, y: 0 });
      batchDraw();
      return;
    }

    const bounds = data.rooms.reduce(
      (acc, room) => {
        const minX = Math.min(acc.minX, room.position.x);
        const maxX = Math.max(acc.maxX, room.position.x + room.position.width);
        const minY = Math.min(acc.minY, room.position.y);
        const maxY = Math.max(acc.maxY, room.position.y + room.position.height);
        return { minX, maxX, minY, maxY };
      },
      { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
    );

    const contentWidth = Math.max(1, bounds.maxX - bounds.minX);
    const contentHeight = Math.max(1, bounds.maxY - bounds.minY);

    // Padding so content doesn't touch edges
    const padding = 100;
    const paddedWidth = contentWidth + padding * 2;
    const paddedHeight = contentHeight + padding * 2;

    const scaleX = canvasDimensions.width / paddedWidth;
    const scaleY = canvasDimensions.height / paddedHeight;
    const scale = Math.min(scaleX, scaleY, 1); // never upscale beyond 1 for crispness

    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;

    const x = canvasDimensions.width / 2 - centerX * scale;
    const y = canvasDimensions.height / 2 - centerY * scale;

    setStageScale(scale);
    setStagePosition({ x, y });
    batchDraw();
  }, [data?.rooms, canvasDimensions.width, canvasDimensions.height, batchDraw]);

  // Refit when data or container size changes (ensures no cropping on any screen)
  useEffect(() => {
    fitContentToViewport();
  }, [fitContentToViewport]);

  // PHASE 3: Keyboard navigation per Spec Section 3 & 11
  useKeyboardNavigation({
    rooms: data?.rooms || [],
    selectedRoom,
    onSelectRoom: (room) => {
      setSelectedRoom(room as FloorPlanRoom);
      if (room) {
        onRoomClick?.(room as FloorPlanRoom);
      }
    },
    onOpenAssignModal: (room) => {
      onRoomDoubleClick?.(room as FloorPlanRoom);
    },
    onZoomIn: handleZoomIn,
    onZoomOut: handleZoomOut,
    onResetZoom: handleResetView,
    enabled: !!data?.rooms && data.rooms.length > 0,
    roomsPerRow: 4
  });

  const handleToggleGridSnap = useCallback(() => {
    setGridSnapEnabled(prev => !prev);
  }, []);

  const handleExportView = useCallback(() => {
    if (stageRef.current) {
      const dataURL = stageRef.current.toDataURL({
        mimeType: 'image/png',
        quality: 1,
        pixelRatio: 2
      });
      
      const link = document.createElement('a');
      link.download = `floor-plan-${new Date().toISOString().split('T')[0]}.png`;
      link.href = dataURL;
      link.click();
    }
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Room hover handlers for tooltips
  const handleRoomMouseEnter = useCallback((e: any, room: FloorPlanRoom) => {
    const stage = e.target.getStage();
    const mousePos = stage.getPointerPosition();
    
    if (mousePos) {
      // Clear any existing timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      
      // PHASE 3: 120ms delay per Spec Section 5
      tooltipTimeoutRef.current = setTimeout(() => {
        // NEW: Build enhanced tooltip with conflict info
        let tooltipText = `Room ${room.number}: ${room.name}\nCapacity: ${room.assignments.length}/${room.capacity}`;
      
      // Add conflict information if exists
      if (hasConflicts(room)) {
        const conflict = room.conflicts?.[0];
        if (conflict) {
          tooltipText += `\n\n⚠️ CONFLICT: ${conflict.message}`;
        } else if (room.assignments.length > room.capacity) {
          tooltipText += `\n\n⚠️ CONFLICT: Capacity exceeded (${room.assignments.length}/${room.capacity})`;
        }
        
        // Add suggestion from conflictSuggestions map
        const suggestion = conflictSuggestions.get(room.id);
        if (suggestion?.suggestion) {
          tooltipText += `\n💡 Suggestion: ${suggestion.suggestion.message}`;
        }
      }
      
        const conflictData = room.conflicts?.[0] && conflictSuggestions.get(room.id) ? {
          conflictCode: room.conflicts[0].code,
          message: room.conflicts[0].message,
          severity: room.conflicts[0].severity,
          suggestion: conflictSuggestions.get(room.id)?.suggestion
        } : undefined;
        
        setTooltip({
          visible: true,
          x: mousePos.x,
          y: mousePos.y - 10,
          text: tooltipText,
          ...(conflictData && { conflict: conflictData as ConflictSuggestion })
        });
        batchDraw();
      }, 120); // 120ms delay per Spec Section 5
    }
  }, [batchDraw, conflictSuggestions]);

  const handleRoomMouseLeave = useCallback(() => {
    // Clear tooltip delay timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = undefined;
    }
    setTooltip({ visible: false, x: 0, y: 0, text: '' });
    batchDraw();
  }, [batchDraw]);

  const handleRoomMouseMove = useCallback((e: any) => {
    const stage = e.target.getStage();
    const mousePos = stage.getPointerPosition();
    
    if (mousePos && tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: mousePos.x,
        y: mousePos.y - 10
      }));
    }
  }, [tooltip.visible]);

  // Computed values
  const roomCount = data?.rooms?.length || 0;
  const assignmentCount = useMemo(() => {
    return data?.rooms?.reduce((total, room) => total + room.assignments.length, 0) || 0;
  }, [data?.rooms]);

  const conflictCount = useMemo(() => {
    return data?.rooms?.reduce((total, room) => {
      const hasConflicts = room.assignments.some(assignment => 
        assignment.status === 'CANCELLED' || 
        (selectedDate && room.blocks.some(block => {
          const startDate = new Date(block.startDate);
          const endDate = new Date(block.endDate);
          return selectedDate >= startDate && selectedDate <= endDate;
        }))
      );
      return hasConflicts ? total + 1 : total;
    }, 0) || 0;
  }, [data?.rooms, selectedDate]);

  // Legend items
  const legendItems = useMemo(() => [
    {
      id: 'available',
      label: 'Available',
      color: ROOM_COLORS.available,
      count: data?.rooms?.filter(room => getRoomStatusColor(room, selectedDate) === ROOM_COLORS.available).length || 0
    },
    {
      id: 'occupied',
      label: 'Occupied',
      color: ROOM_COLORS.occupied,
      count: data?.rooms?.filter(room => getRoomStatusColor(room, selectedDate) === ROOM_COLORS.occupied).length || 0
    },
    {
      id: 'blocked',
      label: 'Blocked',
      color: ROOM_COLORS.blocked,
      count: data?.rooms?.filter(room => getRoomStatusColor(room, selectedDate) === ROOM_COLORS.blocked).length || 0
    },
    {
      id: 'inactive',
      label: 'Inactive',
      color: ROOM_COLORS.inactive,
      count: data?.rooms?.filter(room => getRoomStatusColor(room, selectedDate) === ROOM_COLORS.inactive).length || 0
    }
  ], [data?.rooms, selectedDate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (batchDrawTimeoutRef.current) {
        clearTimeout(batchDrawTimeoutRef.current);
      }
    };
  }, []);
  
  // Loading state - Enhanced with better UX
  if (loading) {
    return (
      <Paper p="xl" radius="md" withBorder>
        <Center>
          <Stack align="center" gap="md">
            <Box
              style={{
                background: 'linear-gradient(135deg, #228BE6 0%, #339AF0 100%)',
                borderRadius: '50%',
                padding: '20px',
                boxShadow: '0 8px 32px rgba(34, 139, 230, 0.3)',
              }}
            >
              <Loader size="xl" color="white" />
            </Box>
            <MantineText c="dimmed" fw={500}>Loading floor plan...</MantineText>
            <MantineText c="dimmed" size="sm">Fetching room data and assignments</MantineText>
          </Stack>
        </Center>
      </Paper>
    );
  }
  
  // Error state - Enhanced with EmptyStateIllustration
  if (error) {
    return (
      <Paper p="xl" radius="md" withBorder>
        <EmptyStateIllustration
          type="error"
          title="Failed to load floor plan"
          description={error}
          action={
            <AnimatedButton
              variant="primary"
              leftSection={<IconCalendar size={16} />}
              onClick={() => window.location.reload()}
              size="sm"
            >
              Retry
            </AnimatedButton>
          }
          size="md"
        />
      </Paper>
    );
  }
  
  // No rooms state - Show actionable empty state with Create Room and Edit Mode options
  if (!data || !data.rooms || data.rooms.length === 0) {
    return (
      <Paper p="xl" radius="md" withBorder>
        <Stack align="center" gap="lg">
          <EmptyStateIllustration
            type="no-rooms"
            title="No rooms on this floor yet"
            description="This floor doesn't have any rooms yet. Create a room to get started, then use Edit Mode to position it on the floor plan."
            size="md"
          />
          {canEditLayout && (
            <Group gap="md">
              <Button
                leftSection={<IconPlus size={16} />}
                size="lg"
                variant="filled"
                onClick={() => onCreateRoom?.()}
              >
                Create Room
              </Button>
              <Text c="dimmed" size="sm">or</Text>
              <Button
                leftSection={<IconEdit size={16} />}
                size="lg"
                variant="light"
                onClick={() => onEnterEditMode?.()}
              >
                Enter Edit Mode
              </Button>
            </Group>
          )}
        </Stack>
      </Paper>
    );
  }
  
  return (
    <Paper 
      radius="md" 
      withBorder 
      style={{ 
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          borderRadius: 0
        })
      }}
    >
      {/* Enhanced Toolbar */}
      {showToolbar && (
        <FloorPlanToolbar
          zoom={stageScale}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetView={handleResetView}
          onToggleGridSnap={handleToggleGridSnap}
          onExportView={handleExportView}
          onToggleFullscreen={handleToggleFullscreen}
          onToggleEditMode={() => setIsEditMode(!isEditMode)}
          gridSnapEnabled={gridSnapEnabled}
          isFullscreen={isFullscreen}
          isEditMode={isEditMode}
          canEdit={canEditLayout}
          roomCount={roomCount}
          assignmentCount={assignmentCount}
          conflictCount={conflictCount}
          loading={loading}
        />
      )}

      {/* Header */}
      <Box p="md" bg="gray.0">
        <Stack gap="xs">
          <MantineText fw={600} size="lg">
            {data.floor.building.name} - {data.floor.name}
          </MantineText>
          {selectedDate && (
            <MantineText c="dimmed" size="sm">
              Viewing assignments for {selectedDate.toLocaleDateString()}
            </MantineText>
          )}
        </Stack>
      </Box>
      
      {/* Canvas - Responsive, fills remaining viewport height */}
      <Box 
        ref={containerRef}
        style={{ 
          position: 'relative',
          width: '100%',
          height: '100%',
          flex: 1,
          overflow: 'hidden'
        }}
        role="application"
        aria-label="Interactive floor plan viewer"
        aria-roledescription="Canvas showing hospital room layout with interactive elements"
      >
        {/* Conditional Rendering: Edit Mode vs Live View */}
        {isEditMode ? (
          <FloorPlanEditor
            floorId={data.floor.id}
            rooms={data.rooms}
            onExitEditMode={() => setIsEditMode(false)}
            stageWidth={canvasDimensions.width}
            stageHeight={canvasDimensions.height}
          />
        ) : (
          <Stage
          ref={stageRef}
          width={canvasDimensions.width}
          height={canvasDimensions.height}
          scaleX={stageScale}
          scaleY={stageScale}
          x={stagePosition.x}
          y={stagePosition.y}
          onDragEnd={handleStageDragEnd}
          onWheel={handleWheel}
          draggable
        >
          {/* LAYER 1: Background Grid (Static, No Event Listening) */}
          <Layer listening={false}>
            <GridComponent />
          </Layer>
          
          {/* LAYER 2: Rooms (Interactive Main Layer) */}
          <Layer ref={layerRef}>
            {data.rooms.map((room) => {
              // PHASE 4: Calculate opacity based on legend filters
              const roomOpacity = getRoomOpacityForLegendFilter(
                room,
                activeLegendFilters,
                selectedDate
              );
              
              return (
                <RoomNode
                  key={room.id}
                  room={room}
                  color={getRoomStatusColor(room, selectedDate)}
                  opacity={roomOpacity}
                  isSelected={selectedRoom?.id === room.id}
                  onClick={() => handleRoomClick(room)}
                  onContextMenu={handleRoomContextMenu}
                  onMouseEnter={handleRoomMouseEnter}
                  onMouseLeave={handleRoomMouseLeave}
                  onMouseMove={handleRoomMouseMove}
                />
              );
            })}
          </Layer>
          
          {/* LAYER 3: Effects and Pulses (Conflict Animations) */}
          <Layer listening={false}>
            {/* Reserved for future conflict pulse effects */}
          </Layer>
          
          {/* LAYER 4: Tooltips (Dynamic Overlay) */}
          <Layer listening={false}>
            {tooltip.visible && (
              <Group>
                {/* NEW: Dynamic tooltip size based on content */}
                <Rect
                  x={tooltip.x}
                  y={tooltip.y}
                  width={tooltip.conflict ? 280 : 220}
                  height={tooltip.conflict ? 110 : 60}
                  fill={tooltip.conflict ? 'rgba(254, 242, 242, 0.98)' : 'rgba(0, 0, 0, 0.85)'}
                  stroke={tooltip.conflict ? '#FA5252' : ''}
                  strokeWidth={tooltip.conflict ? 2 : 0}
                  cornerRadius={8}
                  shadowColor="black"
                  shadowBlur={10}
                  shadowOpacity={0.4}
                />
                {/* Multi-line text support */}
                <Text
                  x={tooltip.x + 12}
                  y={tooltip.y + 12}
                  text={tooltip.text}
                  fontSize={12}
                  fill={tooltip.conflict ? '#1F2937' : 'white'}
                  fontStyle={tooltip.conflict ? 'normal' : 'normal'}
                  width={tooltip.conflict ? 256 : 196}
                  lineHeight={1.4}
                />
              </Group>
            )}
          </Layer>
          
          {/* LAYER 5: Selection Overlay (Highlight Borders) */}
          <Layer listening={false}>
            {selectedRoom && (
              <Rect
                x={selectedRoom.position.x - 2}
                y={selectedRoom.position.y - 2}
                width={selectedRoom.position.width + 4}
                height={selectedRoom.position.height + 4}
                stroke="#74b9ff"
                strokeWidth={3}
                cornerRadius={10}
                dash={[8, 4]}
                listening={false}
              />
            )}
          </Layer>
        </Stage>
        )}
        
        {/* Enhanced Legend - Always visible with new props (only in Live View) */}
        {showLegend && !isEditMode && (
          <FloorPlanLegend
            items={legendItems}
            position={legendPosition}
            compact={isFullscreen}
            showCounts={true}
            alwaysVisible={legendAlwaysVisible}
            activeFilters={activeLegendFilters}
            {...(onLegendFilterChange && { onItemClick: onLegendFilterChange })}
            {...(onClearLegendFilters && { onClearFilters: onClearLegendFilters })}
          />
        )}
      </Box>
      
      {/* PHASE 3: Context Menu - Per Spec Section 3 */}
      {contextMenuPosition && (
        <RoomContextMenu
          room={data.rooms.find(r => r.id === contextMenuPosition.roomId) as RoomForContextMenu || null}
          position={{ x: contextMenuPosition.x, y: contextMenuPosition.y }}
          selectedDate={selectedDate}
          opened={true}
          onClose={() => setContextMenuPosition(null)}
          onAction={(action, room) => {
            onContextMenuAction?.(action, room as FloorPlanRoom);
            setContextMenuPosition(null);
          }}
        />
      )}
      
      {/* PHASE 6: Off-Canvas Accessibility List - Per Spec Section 11 */}
      {data.rooms && data.rooms.length > 0 && (
        <OffCanvasRoomList
          rooms={data.rooms}
          selectedRoomId={selectedRoom?.id || undefined}
          onSelectRoom={(roomId) => {
            const room = data.rooms.find(r => r.id === roomId);
            if (room) handleRoomClick(room);
          }}
          onAssignDoctor={(roomId) => {
            const room = data.rooms.find(r => r.id === roomId);
            if (room) onRoomDoubleClick?.(room);
          }}
          onCreateBlock={(roomId) => {
            const room = data.rooms.find(r => r.id === roomId);
            if (room) onContextMenuAction?.('create-block', room);
          }}
          onViewSchedule={(roomId) => {
            const room = data.rooms.find(r => r.id === roomId);
            if (room) onRoomClick?.(room);
          }}
        />
      )}
    </Paper>
  );
};

export default FloorPlanCanvas;
